import React, { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../firebase';
import { toast, ToastContainer } from 'react-toastify';
import { Search, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import "react-toastify/dist/ReactToastify.css";

const ReactivateConfirmModal = ({ job, onConfirm, onCancel }) => {
  const [notifyEmployer, setNotifyEmployer] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-6">
          <h3 className="text-lg font-bold text-blue-950 mb-2">Reactivate Job</h3>
          <p className="text-gray-600 mb-4">
            "{job.jobTitle}" will become visible to candidates again immediately.
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-700 mb-6">
            <input
              type="checkbox"
              checked={notifyEmployer}
              onChange={(e) => setNotifyEmployer(e.target.checked)}
              className="h-4 w-4"
            />
            Send the employer a "your listing is back up" email
          </label>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(notifyEmployer)}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Reactivate Job
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UnlistedJobs = () => {
  const [unlistedJobs, setUnlistedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [reactivateTarget, setReactivateTarget] = useState(null);

  const db = getDatabase(app);
  const auth = getAuth(app);

  const fetchUnlistedJobs = useCallback(async () => {
    try {
      const jobsRef = ref(db, 'jobs');
      const snapshot = await get(jobsRef);

      if (snapshot.exists()) {
        const jobs = [];
        Object.entries(snapshot.val()).forEach(([employerId, employerJobs]) => {
          Object.entries(employerJobs).forEach(([jobId, job]) => {
            if (job.status === 'unlisted') {
              jobs.push({
                id: jobId,
                employerId,
                ...job
              });
            }
          });
        });
        jobs.sort((a, b) => new Date(b.unlistedAt || 0) - new Date(a.unlistedAt || 0));
        setUnlistedJobs(jobs);
      } else {
        setUnlistedJobs([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching unlisted jobs:', error);
      toast.error('Error loading unlisted jobs');
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    const checkAdminAndFetchJobs = async () => {
      if (!auth.currentUser) return;

      try {
        const adminRef = ref(db, `admins/${auth.currentUser.uid}`);
        const adminSnapshot = await get(adminRef);

        if (!adminSnapshot.exists()) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        setIsAdmin(true);
        await fetchUnlistedJobs();
      } catch (error) {
        console.error('Error checking admin status:', error);
        toast.error('Error verifying admin access');
        setLoading(false);
      }
    };

    checkAdminAndFetchJobs();
  }, [auth.currentUser, db, fetchUnlistedJobs]);

  const toggleJobExpansion = (jobId) => {
    setExpandedJobs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  const handleReactivate = async (job, notifyEmployer) => {
    try {
      const jobRef = ref(db, `jobs/${job.employerId}/${job.id}`);
      const now = new Date();

      await update(jobRef, {
        status: 'active',
        unlistedAt: null,
        unlistedBy: null,
        reactivatedAt: now.toISOString(),
        updatedAt: now.toISOString()
      });

      setUnlistedJobs(prev => prev.filter(j => j.id !== job.id));
      setReactivateTarget(null);
      toast.success('Job reactivated successfully');

      if (notifyEmployer) {
        try {
          const functions = getFunctions(app);
          const notifyJobReactivated = httpsCallable(functions, 'notifyJobReactivated');
          await notifyJobReactivated({ ...job, id: job.id });
        } catch (notifyError) {
          console.error('Error sending reactivation notification email:', notifyError);
          toast.error('Job reactivated, but the notification email failed to send.');
        }
      }
    } catch (error) {
      console.error('Error reactivating job:', error);
      toast.error('Failed to reactivate job. Please try again.');
    }
  };

  const filteredJobs = unlistedJobs.filter(job => {
    const searchLower = searchTerm.toLowerCase();
    return (
      job.jobTitle?.toLowerCase().includes(searchLower) ||
      job.companyName?.toLowerCase().includes(searchLower) ||
      job.description?.toLowerCase().includes(searchLower) ||
      job.parish?.toLowerCase().includes(searchLower) ||
      job.industry?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return <div className="flex justify-center items-center h-96">Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <p className="text-red-600 text-lg font-semibold mb-2">Access Denied</p>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={1}
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-blue-950">Unlisted Jobs</h1>
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <p className="text-gray-600">
            {searchTerm ? 'No jobs match your search criteria' : 'No unlisted jobs'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <div key={job.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-semibold text-blue-950">
                      {job.jobTitle}
                    </h2>
                    <div className="text-sm text-gray-600 mt-1">
                      {job.companyName} • {job.parish}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Unlisted {job.unlistedAt ? new Date(job.unlistedAt).toLocaleDateString() : 'date unknown'}
                      {' • '}
                      {job.unlistedBy === 'admin' ? 'by admin' : 'by employer'}
                    </div>
                  </div>
                  <button
                    onClick={() => setReactivateTarget(job)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reactivate
                  </button>
                </div>
              </div>

              <div className="p-4 pt-0">
                <div className="flex flex-wrap gap-2 mb-2">
                  <span className="px-2 py-1 text-sm rounded-full bg-blue-100 text-blue-800">
                    {job.employmentType}
                  </span>
                  <span className="px-2 py-1 text-sm rounded-full bg-purple-100 text-purple-800">
                    {job.industry}
                  </span>
                  <span className="px-2 py-1 text-sm rounded-full bg-green-100 text-green-800">
                    {job.experience}
                  </span>
                </div>

                <div className="relative">
                  <div className={`prose max-w-none ${!expandedJobs[job.id] ? 'max-h-24 overflow-hidden' : ''}`}>
                    <p className="text-gray-700">{job.description}</p>
                  </div>

                  <button
                    onClick={() => toggleJobExpansion(job.id)}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center mt-2"
                  >
                    {expandedJobs[job.id] ? (
                      <>Show Less <ChevronUp className="ml-1 h-4 w-4" /></>
                    ) : (
                      <>Show More <ChevronDown className="ml-1 h-4 w-4" /></>
                    )}
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold">Required Education:</p>
                    <p className="text-gray-600">{job.degreeLevel}</p>
                  </div>
                  <div>
                    <p className="font-semibold">Salary:</p>
                    <p className="text-gray-600">{job.salary || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="font-semibold">Contact:</p>
                    <p className="text-gray-600">{job.applicationEmail}</p>
                  </div>
                  <div>
                    <p className="font-semibold">Posted:</p>
                    <p className="text-gray-600">
                      {new Date(job.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {reactivateTarget && (
        <ReactivateConfirmModal
          job={reactivateTarget}
          onConfirm={(notifyEmployer) => handleReactivate(reactivateTarget, notifyEmployer)}
          onCancel={() => setReactivateTarget(null)}
        />
      )}
    </div>
  );
};

export default UnlistedJobs;
